import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { Title } from 'react-admin'
import {
  ColumnOrderState,
  flexRender,
  getCoreRowModel,
  OnChangeFn,
  SortingState,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

import useLogViewer from './useLogViewer'
import { LogLevel, SortField } from './types'
import LogToolbar from './components/LogToolbar'
import LogEntryDetail from './components/LogEntryDetail'
import LogSkeleton from './components/LogSkeleton'
import LogPaginationComponent from './components/LogPagination'
import ExportButton from './components/ExportButton'
import CopyLinkButton from './components/CopyLinkButton'
import ColumnSettingsButton from './components/ColumnSettingsButton'
import { buildLogColumns } from './components/logColumns'
import useTranslate, { Translate } from './useTranslate'
import useStyles from './styles'

const AUTO_REFRESH_MS = 5 * 60 * 1000
const COLUMN_VISIBILITY_STORAGE_KEY = 'logViewer.columnVisibility'
const COLUMN_ORDER_STORAGE_KEY = 'logViewer.columnOrder'

// Restores a persisted column setting; malformed/absent storage falls back.
function loadStoredSetting<T>(key: string, fallback: T): T {
  try {
    const stored = localStorage.getItem(key)
    return stored != null ? (JSON.parse(stored) as T) : fallback
  } catch {
    return fallback
  }
}

// Module-level client so the cache survives leaving and re-entering the
// screen; the provider is screen-local because the admin app has no global
// QueryClientProvider. Failed requests are surfaced immediately (retry: false)
// — the user has an explicit Retry button.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
})

function formatFreshness(lastFetchedAt: number | null, t: Translate): string {
  if (lastFetchedAt == null) return ''
  const elapsed = Date.now() - lastFetchedAt
  const elapsedMin = Math.floor(elapsed / 60000)
  const elapsedSec = Math.floor((elapsed % 60000) / 1000)
  const remaining = Math.max(0, AUTO_REFRESH_MS - elapsed)
  const remainingMin = Math.floor(remaining / 60000)
  const remainingSec = Math.floor((remaining % 60000) / 1000)

  const agoText =
    elapsedMin > 0
      ? t('log_viewer.min_ago', { n: elapsedMin })
      : t('log_viewer.sec_ago', { n: elapsedSec })
  const nextText =
    remainingMin > 0
      ? t('log_viewer.min', { n: remainingMin })
      : t('log_viewer.sec', { n: remainingSec })

  return t('log_viewer.freshness', { ago: agoText, next: nextText })
}

const LogViewer: React.FC = () => {
  const classes = useStyles()
  const t = useTranslate()

  const levelStripClasses: Record<LogLevel, string> = {
    ERROR: classes.logRowStripError,
    WARN: classes.logRowStripWarn,
    INFO: classes.logRowStripInfo,
    DEBUG: classes.logRowStripDebug,
  }

  const {
    entries,
    pagination,
    filters,
    loading,
    refreshing,
    error,
    expandedEntryId,
    lastFetchedAt,
    setFilters,
    resetFilters,
    setExpandedEntryId,
    refresh,
  } = useLogViewer()

  const [freshnessText, setFreshnessText] = useState('')
  const scrollAreaRef = useRef<HTMLDivElement>(null)

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => loadStoredSetting<VisibilityState>(COLUMN_VISIBILITY_STORAGE_KEY, {}),
  )
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(() =>
    loadStoredSetting<ColumnOrderState>(COLUMN_ORDER_STORAGE_KEY, []),
  )

  useEffect(() => {
    try {
      localStorage.setItem(
        COLUMN_VISIBILITY_STORAGE_KEY,
        JSON.stringify(columnVisibility),
      )
      localStorage.setItem(
        COLUMN_ORDER_STORAGE_KEY,
        JSON.stringify(columnOrder),
      )
    } catch {
      // Storage can be unavailable (private mode); the settings won't stick.
    }
  }, [columnVisibility, columnOrder])

  useEffect(() => {
    if (loading) scrollAreaRef.current?.scrollTo({ top: 0 })
  }, [loading])

  useEffect(() => {
    const update = (): void =>
      setFreshnessText(formatFreshness(lastFetchedAt, t))
    update()
    const interval = setInterval(update, 1000)
    return () => clearInterval(interval)
  }, [lastFetchedAt, t])

  const columns = useMemo(
    () => buildLogColumns(classes, filters.search, t),
    [classes, filters.search, t],
  )

  const handleCorrelate = useCallback(
    (field: 'traceId' | 'requestId', value: string) => {
      setFilters({
        traceId: field === 'traceId' ? value : '',
        requestId: field === 'requestId' ? value : '',
        subsystems: [],
        eventTypes: [],
        users: [],
        states: [],
        search: '',
      })
    },
    [setFilters],
  )
  // Sorting is server-side: the table only *displays* the sort state, which
  // lives in the filters. Mapping: desc = 'newest' (Z→A / newest first),
  // asc = 'oldest' (A→Z / oldest first). Changing it updates the filters,
  // which changes the query key — a new request fires and the skeleton shows
  // while it is pending, exactly like a filter or page change.
  const sorting = useMemo<SortingState>(
    () => [{ id: filters.sortField, desc: filters.sortOrder === 'newest' }],
    [filters.sortField, filters.sortOrder],
  )

  const handleSortingChange: OnChangeFn<SortingState> = useCallback(
    updater => {
      const next = typeof updater === 'function' ? updater(sorting) : updater
      const active = next[0]
      setFilters({
        sortField: (active?.id as SortField) ?? 'timestamp',
        sortOrder: active == null || active.desc ? 'newest' : 'oldest',
      })
    },
    [sorting, setFilters],
  )

  // Filtering on a single level makes the Level column redundant — every row
  // would show the same chip — so it is hidden while the filter is active.
  // The forced value lives outside columnVisibility on purpose: the user's
  // persisted preference is untouched and the column returns when the filter
  // goes back to ALL.
  const effectiveColumnVisibility = useMemo<VisibilityState>(
    () =>
      filters.level === 'ALL'
        ? columnVisibility
        : { ...columnVisibility, level: false },
    [columnVisibility, filters.level],
  )

  const table = useReactTable({
    data: entries,
    columns,
    state: {
      columnVisibility: effectiveColumnVisibility,
      columnOrder,
      sorting,
    },
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    onSortingChange: handleSortingChange,
    manualSorting: true,
    // One column is always sorted (timestamp by default); a third click flips
    // direction again instead of clearing the sort.
    enableSortingRemoval: false,
    getCoreRowModel: getCoreRowModel(),
    getRowId: row => row.id,
    columnResizeMode: 'onChange',
  })

  const gridTemplateColumns = table
    .getVisibleLeafColumns()
    .map(column =>
      column.getCanResize() ? `${column.getSize()}px` : 'minmax(200px, 1fr)',
    )
    .join(' ')

  const allColumnsHidden = table.getVisibleLeafColumns().length === 0

  return (
    <div className={classes.root}>
      <Title title={t('log_viewer.title')} />

      <div className={classes.pageHeader}>
        <div>
          <h1 className={classes.pageTitle}>{t('log_viewer.title')}</h1>
          <p className={classes.pageSubtitle}>{t('log_viewer.subtitle')}</p>
        </div>
        <div className={classes.headerActions}>
          {freshnessText !== '' && (
            <span className={classes.freshnessText}>{freshnessText}</span>
          )}
          <button
            className={classes.refreshButton}
            onClick={refresh}
            disabled={refreshing}
          >
            <span
              className={`material-symbols-outlined${
                refreshing ? ` ${classes.refreshIconSpinning}` : ''
              }`}
              style={{ fontSize: 16 }}
            >
              refresh
            </span>
            {t('log_viewer.refresh')}
          </button>
          <ColumnSettingsButton
            table={table}
            disabledColumnIds={filters.level !== 'ALL' ? ['level'] : []}
          />
          <CopyLinkButton />
          <ExportButton entries={entries} />
        </div>
      </div>

      <LogToolbar
        filters={filters}
        onFilterChange={setFilters}
        onClearFilters={resetFilters}
      />

      <div className={classes.tableContainer}>
        {!allColumnsHidden && (
          <div
            className={`${classes.logGrid} ${classes.logStreamHeader}`}
            style={{ gridTemplateColumns }}
          >
            {table.getHeaderGroups().map(headerGroup =>
              headerGroup.headers.map(header => {
                const sortDir = header.column.getIsSorted()
                return (
                  <div
                    key={header.id}
                    className={classes.logHeaderCell}
                    aria-sort={
                      sortDir === 'asc'
                        ? 'ascending'
                        : sortDir === 'desc'
                        ? 'descending'
                        : undefined
                    }
                  >
                    {header.column.getCanSort() ? (
                      <button
                        type='button'
                        className={classes.headerSortButton}
                        onClick={header.column.getToggleSortingHandler()}
                        title={`Sort by ${String(
                          header.column.columnDef.header,
                        )}`}
                      >
                        <span className={classes.headerSortLabel}>
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                        </span>
                        <span className={classes.headerSortArrows}>
                          <span
                            className={`material-symbols-outlined ${
                              classes.headerSortArrow
                            }${
                              sortDir === 'desc'
                                ? ` ${classes.headerSortArrowActive}`
                                : ''
                            }`}
                            style={{ fontSize: 14, lineHeight: '7px' }}
                          >
                            arrow_drop_up
                          </span>
                          <span
                            className={`material-symbols-outlined ${
                              classes.headerSortArrow
                            }${
                              sortDir === 'asc'
                                ? ` ${classes.headerSortArrowActive}`
                                : ''
                            }`}
                            style={{ fontSize: 14, lineHeight: '7px' }}
                          >
                            arrow_drop_down
                          </span>
                        </span>
                      </button>
                    ) : (
                      flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )
                    )}
                    {header.column.getCanResize() && (
                      <div
                        role='separator'
                        onMouseDown={header.getResizeHandler()}
                        onTouchStart={header.getResizeHandler()}
                        onClick={e => e.stopPropagation()}
                        className={`${classes.resizer} ${
                          header.column.getIsResizing()
                            ? classes.resizerActive
                            : ''
                        }`}
                      />
                    )}
                  </div>
                )
              }),
            )}
          </div>
        )}

        <div
          ref={scrollAreaRef}
          className={classes.scrollArea}
        >
          {allColumnsHidden && (
            <div className={classes.emptyState}>
              <span
                className='material-symbols-outlined'
                style={{ fontSize: 48, opacity: 0.4 }}
              >
                visibility_off
              </span>
              <span>All columns are hidden.</span>
              <button
                className={classes.refreshButton}
                onClick={() => setColumnVisibility({})}
              >
                Show all columns
              </button>
            </div>
          )}

          {!allColumnsHidden && loading && (
            <LogSkeleton
              rows={Math.min(filters.limit, 25)}
              gridTemplateColumns={gridTemplateColumns}
              columnCount={table.getVisibleLeafColumns().length}
            />
          )}

          {error != null && (
            <div className={classes.errorContainer}>
              <span
                className='material-symbols-outlined'
                style={{ fontSize: 32 }}
              >
                error
              </span>
              <span>{error}</span>
              <button
                className={classes.refreshButton}
                onClick={refresh}
              >
                Retry
              </button>
            </div>
          )}

          {!allColumnsHidden &&
            !loading &&
            error == null &&
            entries.length === 0 && (
              <div className={classes.emptyState}>
                <span
                  className='material-symbols-outlined'
                  style={{ fontSize: 48, opacity: 0.4 }}
                >
                  search_off
                </span>
                <span>No log entries match the current filters.</span>
              </div>
            )}

          {!allColumnsHidden &&
            !loading &&
            error == null &&
            entries.length > 0 && (
              <div className={classes.logList}>
                {table.getRowModel().rows.map(row => {
                  const entry = row.original
                  const isExpanded = expandedEntryId === entry.id
                  const rowClass = `${classes.logGrid} ${classes.logRow} ${
                    row.index % 2 === 0 ? classes.logRowEven : classes.logRowOdd
                  }`
                  return (
                    <React.Fragment key={row.id}>
                      <div
                        className={rowClass}
                        style={{ gridTemplateColumns }}
                        onClick={() =>
                          setExpandedEntryId(isExpanded ? null : entry.id)
                        }
                      >
                        <div
                          className={`${classes.logRowStrip} ${
                            levelStripClasses[entry.level] ?? ''
                          }`}
                        />
                        {row.getVisibleCells().map(cell => (
                          <React.Fragment key={cell.id}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                      {isExpanded && (
                        <LogEntryDetail
                          entry={entry}
                          onCorrelate={handleCorrelate}
                        />
                      )}
                    </React.Fragment>
                  )
                })}
              </div>
            )}
        </div>

        {pagination != null && (
          <LogPaginationComponent
            pagination={pagination}
            onPageChange={page => setFilters(prev => ({ ...prev, page }))}
            onLimitChange={limit => setFilters({ limit })}
          />
        )}
      </div>
    </div>
  )
}

const LogViewerScreen: React.FC = () => (
  <QueryClientProvider client={queryClient}>
    <LogViewer />
  </QueryClientProvider>
)

export default LogViewerScreen
